import { DEFAULT_IMAGE_COMPRESSION, DEFAULT_IMAGE_MIRROR, DEFAULT_SIZE_FACTOR } from "./constants";
import { MediaServices, type CameraResolution, type DataUriConfig } from "./MediaServices";

export interface CameraPhotoConfig extends Partial<DataUriConfig> {}

export class CameraPhoto {
	private stream: MediaStream | null = null;
	private numberOfMaxResolutionTry = 0;
	private settings: MediaTrackSettings | null = null;
	private readonly mediaDevices: ReturnType<typeof MediaServices.getNavigatorMediaDevices>;

	constructor(private readonly videoElement: HTMLVideoElement) {
		this.mediaDevices = MediaServices.getNavigatorMediaDevices();
	}

	private async getStreamDevice(idealCameraDevice?: string, idealResolution?: CameraResolution) {
		if (!this.mediaDevices) throw new Error("Camera access is not supported by this browser");
		const stream = await this.mediaDevices.getUserMedia(
			MediaServices.getIdealConstraints(idealCameraDevice, idealResolution),
		);
		this.gotStream(stream);
		return stream;
	}

	private async getStreamDeviceMaxResolution(idealCameraDevice: string) {
		const constraints = MediaServices.getMaxResolutionConstraints(idealCameraDevice, this.numberOfMaxResolutionTry);
		if (!constraints) return this.getStreamDevice(idealCameraDevice, {});
		try {
			if (!this.mediaDevices) throw new Error("Camera access is not supported by this browser");
			const stream = await this.mediaDevices.getUserMedia(constraints);
			this.gotStream(stream);
			return stream;
		} catch (error) {
			this.numberOfMaxResolutionTry += 1;
			if (this.numberOfMaxResolutionTry > 9) throw error;
			await new Promise((resolve) => setTimeout(resolve, 20));
			return this.getStreamDeviceMaxResolution(idealCameraDevice);
		}
	}

	private setVideoSrc(stream: MediaStream) {
		this.videoElement.srcObject = stream;
	}

	private setSettings(stream: MediaStream | null) {
		this.settings = stream?.getTracks()[0]?.getSettings() ?? null;
	}

	private gotStream(stream: MediaStream) {
		this.stream = stream;
		this.setSettings(stream);
		this.setVideoSrc(stream);
	}

	getCameraSettings() {
		return this.settings;
	}

	async enumerateCameras() {
		if (!this.mediaDevices) throw new Error("Camera access is not supported by this browser");
		if (!this.stream) {
			const stream = await this.mediaDevices.getUserMedia({ video: true });
			stream.getTracks().forEach((track) => track.stop());
		}
		return (await this.mediaDevices.enumerateDevices()).filter(
			(device) => device.kind === "videoinput" && device.deviceId,
		);
	}

	async startCamera(idealCameraDevice?: string, idealResolution?: CameraResolution) {
		await this.stopCamera().catch(() => undefined);
		return this.getStreamDevice(idealCameraDevice, idealResolution);
	}

	async startCameraMaxResolution(idealCameraDevice = "") {
		this.numberOfMaxResolutionTry = 0;
		await this.stopCamera().catch(() => undefined);
		return this.getStreamDeviceMaxResolution(idealCameraDevice);
	}

	getDataUri(userConfig: CameraPhotoConfig = {}) {
		return MediaServices.getDataUri(this.videoElement, {
			sizeFactor: userConfig.sizeFactor ?? DEFAULT_SIZE_FACTOR,
			imageType: userConfig.imageType ?? MediaServices.IMAGE_TYPES.PNG,
			imageCompression: userConfig.imageCompression ?? DEFAULT_IMAGE_COMPRESSION,
			isImageMirror: userConfig.isImageMirror ?? DEFAULT_IMAGE_MIRROR,
		});
	}

	stopCamera() {
		if (!this.stream) return Promise.reject(new Error("no stream to stop!"));
		this.stream.getTracks().forEach((track) => track.stop());
		this.videoElement.srcObject = null;
		this.stream = null;
		this.setSettings(null);
		return Promise.resolve();
	}
}
